'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoint, mb, mr, p, Inline, Box } from '@nib/layout';
import Heading from '@nib-components/heading';
import { formBackgroundColor, copyColor, formLabelColor } from '@nib-components/theme';
import { InfoSystemIcon } from '@nib/icons';
import { Bold } from '@nib-components/copy';
const validFormModes = ['light', 'white'];
const validFormTypes = ['required', 'optional'];
const cloneComponents = ['FormControl', 'DatePicker', 'DateRangePicker'];
export const StyledHeading = styled(Heading)`
  color: var(--themeColorFgLabel, ${formLabelColor});
  width: 100%;
`;
export const FormInput = styled.form`
  box-sizing: border-box;
  ${p(4)};
  ${breakpoint('sm')`
    ${p(6)};
  `}
  /* 
    We really want a transparent background, but there is no token for this so cannot do the fallback that way.
    --themeColorBg is most likely the correct color for the mode.
  */
  background-color: var(--themeColorBg, ${formBackgroundColor});
  color: var(--themeColorFg, ${copyColor});
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const FormContainer = styled(_ref => {
  let {
    containerWidth,
    isCompact,
    spaceChildren,
    ...rest
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  ${_ref2 => {
  let {
    containerWidth
  } = _ref2;
  return containerWidth && `max-width: ${containerWidth};`;
}};
  margin-left: auto;
  margin-right: auto;

  ${_ref3 => {
  let {
    isCompact
  } = _ref3;
  return isCompact && `
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
  `;
}};

  ${_ref4 => {
  let {
    isCompact,
    spaceChildren
  } = _ref4;
  return isCompact && spaceChildren && css`
      > * {
        ${mr(4)};
        ${mb(5)};
      }
    `;
}};

  ${_ref5 => {
  let {
    spaceChildren,
    isCompact
  } = _ref5;
  return spaceChildren && !isCompact && css`
      > * {
        ${mb({
    xs: 5,
    sm: 7
  })};
      }
    `;
}};
`;
export const hasDisplayName = type => {
  return type !== null && (typeof type === 'object' || typeof type === 'function') && 'displayName' in type;
};
const Form = props => {
  const {
    title = '',
    children,
    containerWidth = '768px',
    spaceChildren = true,
    hideFormTypeMessage = false,
    formMode,
    isCompact = false,
    formType,
    titleComponent = 'h3',
    onSubmit = () => {},
    ...other
  } = props;
  const handleSubmit = event => {
    if (!props.action) {
      event.preventDefault();
    }
    // TODO: This is to prevent a breaking change. Should be removed in the next major version
    onSubmit && onSubmit(event);
  };
  return /*#__PURE__*/_jsx(FormInput, {
    "data-mesh-component": "FORM",
    ...other,
    onSubmit: e => handleSubmit(e),
    formMode: formMode,
    children: /*#__PURE__*/_jsxs(FormContainer, {
      containerWidth: containerWidth,
      spaceChildren: spaceChildren,
      isCompact: isCompact,
      children: [title ? /*#__PURE__*/_jsx(StyledHeading, {
        size: 2,
        component: titleComponent,
        children: title
      }) : null, formType && !hideFormTypeMessage && /*#__PURE__*/_jsx(Box, {
        width: "100%",
        lineHeight: "1",
        children: /*#__PURE__*/_jsxs(Inline, {
          space: 2,
          nowrap: true,
          verticalAlign: "center",
          children: [/*#__PURE__*/_jsx(InfoSystemIcon, {
            fill: "info",
            size: 20,
            style: {
              lineHeight: 1
            }
          }), /*#__PURE__*/_jsxs(Bold, {
            color: "info",
            children: ["All fields are ", formType, ", unless marked otherwise."]
          })]
        })
      }), React.Children.map(children, child => {
        if (/*#__PURE__*/React.isValidElement(child)) {
          // If it's a custom React component, check for displayName or name. We want to pass these props only to FormControl
          if (hasDisplayName(child.type) && child.type.displayName && cloneComponents.includes(child.type.displayName)) {
            return /*#__PURE__*/React.cloneElement(child, {
              isCompact,
              formType
            });
          }
        }
        return child;
      })]
    })
  });
};
Form.displayName = 'Form';
export default Form;